import { CenteredLazyLoading } from "@/controls/LazyLoading";
import {
	ClickAwayListener,
	Input,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	useTheme,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { TensileRequirementByGrade } from "../../../models/TensileRequirementByGrade";
import { useTableData } from "../hooks/useTableData";
import { useTableFunctions } from "../hooks/useTableFunctions";
import {
	TensileColumnDef,
	useTensileTableCellEditing,
} from "../hooks/useTensileTableCellEditing";

interface TensileRequirementsByGradeTableProps {
	rows: TensileRequirementByGrade[];
	isLoading: boolean;
	isEditMode: boolean;
	onRowUpdate: (
		rowIndex: number,
		updates: Partial<TensileRequirementByGrade>,
	) => void;
}

export const TensileRequirementsByGradeTable = ({
	rows,
	isLoading,
	isEditMode,
	onRowUpdate,
}: TensileRequirementsByGradeTableProps) => {
	const theme = useTheme();
	const tableContainerRef = useRef<HTMLDivElement>(null);

	const { columns } = useTableData(isEditMode);
	const {
		processFieldUpdate,
		getEditValue,
		getCombinedFieldDisplay,
		isCombinedMinMaxField,
	} = useTableFunctions();

	// Calculate column widths based on flex
	const columnWidths = useMemo(() => {
		const totalFlex = columns.reduce((sum, col) => sum + (col.flex || 0), 0);
		const fixedWidth = columns.reduce(
			(sum, col) => (col.flex ? sum : sum + (col.minWidth || 60)),
			0,
		);
		const flexiblePercent = 100 - fixedWidth / 10;

		return columns.map((col) => {
			if (col.flex && totalFlex > 0) {
				return `${(col.flex / totalFlex) * flexiblePercent}%`;
			}
			return col.minWidth ? `${col.minWidth}px` : "auto";
		});
	}, [columns]);

	const headerGroups = useMemo(() => {
		const groups: {
			headerName: string | null;
			colSpan: number;
			columns: TensileColumnDef[];
		}[] = [];
		let currentGroup: string | undefined;
		let currentColumns: TensileColumnDef[] = [];

		columns.forEach((col) => {
			if (!col.headerGroup) {
				if (currentColumns.length > 0) {
					groups.push({
						headerName: currentGroup || null,
						colSpan: currentColumns.length,
						columns: currentColumns,
					});
					currentColumns = [];
					currentGroup = undefined;
				}
				groups.push({
					headerName: null,
					colSpan: 1,
					columns: [col],
				});
			} else if (col.headerGroup !== currentGroup) {
				if (currentColumns.length > 0) {
					groups.push({
						headerName: currentGroup || null,
						colSpan: currentColumns.length,
						columns: currentColumns,
					});
				}
				currentGroup = col.headerGroup;
				currentColumns = [col];
			} else {
				currentColumns.push(col);
			}
		});

		if (currentColumns.length > 0) {
			groups.push({
				headerName: currentGroup || null,
				colSpan: currentColumns.length,
				columns: currentColumns,
			});
		}

		return groups;
	}, [columns]);

	const hasHeaderGroups = useMemo(
		() => columns.some((col) => col.headerGroup),
		[columns],
	);

	const handleCellUpdate = useCallback(
		(rowIndex: number, field: keyof TensileRequirementByGrade, value: any) => {
			const row = rows[rowIndex];
			if (!row) return;
			const updates = processFieldUpdate(row, field, value);
			onRowUpdate(rowIndex, updates);
		},
		[rows, processFieldUpdate, onRowUpdate],
	);

	const {
		editingCell,
		selectedCell,
		handleCellClick,
		handleCellDoubleClick,
		handleCellChange,
		handleCellBlur,
		handleEditKeyDown,
		clearSelection,
	} = useTensileTableCellEditing({
		rows,
		columns,
		onCellUpdate: handleCellUpdate,
		getEditValue,
	});

	const prevRowCount = useRef(rows.length);

	useEffect(() => {
		if (
			isEditMode &&
			rows.length > prevRowCount.current &&
			tableContainerRef.current
		) {
			tableContainerRef.current.scrollTo({
				top: tableContainerRef.current.scrollHeight,
				behavior: "smooth",
			});
		}
		prevRowCount.current = rows.length;
	}, [rows.length, isEditMode]);

	useEffect(() => {
		if (!selectedCell || !tableContainerRef.current) return;

		const cellElement = tableContainerRef.current.querySelector(
			`[data-cell-id="${selectedCell.rowIndex}-${selectedCell.colIndex}"]`,
		) as HTMLElement;

		if (cellElement) {
			cellElement.scrollIntoView({
				behavior: "smooth",
				block: "center",
				inline: "nearest",
			});
		}
	}, [selectedCell]);

	const renderCellContent = useCallback(
		(
			row: TensileRequirementByGrade,
			col: TensileColumnDef,
			rowIndex: number,
		) => {
			const isEditing =
				editingCell?.rowIndex === rowIndex && editingCell?.field === col.field;

			if (isEditing) {
				return (
					<ClickAwayListener onClickAway={handleCellBlur}>
						<Input
							autoFocus
							value={editingCell.value}
							onChange={(e) => handleCellChange(e.target.value)}
							onKeyDown={handleEditKeyDown}
							onBlur={handleCellBlur}
							sx={{
								fontSize: 12,
								lineHeight: 1.2,
								width: "100%",
								"& input": {
									textAlign: col.align || "center",
									padding: "1px 2px",
								},
							}}
						/>
					</ClickAwayListener>
				);
			}

			// In edit mode, use computed display for combined fields
			// In read-only mode, show the raw text value from backend
			if (isCombinedMinMaxField(col.field)) {
				if (isEditMode) {
					return getCombinedFieldDisplay(row, col.field);
				}
				return row[col.field] ?? "";
			}

			const value = row[col.field];
			if (col.formatValue && value != null) {
				return col.formatValue(value);
			}

			return value ?? "";
		},
		[
			editingCell,
			handleCellBlur,
			handleCellChange,
			handleEditKeyDown,
			isCombinedMinMaxField,
			getCombinedFieldDisplay,
			isEditMode,
		],
	);

	return (
		<Paper
			elevation={2}
			onClick={clearSelection}
			sx={{
				height: "100%",
				width: "100%",
				display: "flex",
				flexDirection: "column",
			}}
		>
			<TableContainer
				ref={tableContainerRef}
				sx={{ flex: 1, overflow: "auto" }}
			>
				<Table
					stickyHeader
					size="small"
					sx={{ width: "100%", tableLayout: "fixed" }}
				>
					<colgroup>
						{columnWidths.map((width, i) => (
							<col key={i} style={{ width }} />
						))}
					</colgroup>
					<TableHead>
						<TableRow>
							{headerGroups.map((group, idx) => {
								const isGrouped = group.colSpan > 1 && group.headerName;
								return (
									<TableCell
										key={idx}
										colSpan={group.colSpan}
										rowSpan={isGrouped ? 1 : 2}
										align="center"
										sx={{
											backgroundColor: "#fff",
											fontWeight: 600,
											fontSize: 11,
											lineHeight: 2,
											borderBottom: isGrouped
												? "1px solid rgba(224, 224, 224, 1)"
												: undefined,
											py: 0,
											verticalAlign: "middle",
											position: "sticky",
											top: 0,
											zIndex: 2,
										}}
									>
										{isGrouped
											? group.headerName
											: group.columns[0]?.headerName}
									</TableCell>
								);
							})}
						</TableRow>
						{hasHeaderGroups && (
							<TableRow>
								{columns
									.filter((col) => col.headerGroup)
									.map((col, idx) => (
										<TableCell
											key={col.field}
											align={col.align || "center"}
											sx={{
												fontSize: 11,
												fontWeight: 600,
												lineHeight: 2,
												py: 0,
												boxSizing: "border-box",
												backgroundColor: "#fff",
												position: "sticky",
												top: 22,
												zIndex: 2,
											}}
										>
											{col.headerName}
										</TableCell>
									))}
							</TableRow>
						)}
					</TableHead>
					<TableBody>
						{isLoading ? (
							<TableRow>
								<TableCell colSpan={columns.length} align="center">
									<CenteredLazyLoading />
								</TableCell>
							</TableRow>
						) : rows.length === 0 ? (
							<TableRow>
								<TableCell colSpan={columns.length} align="center">
									No data found
								</TableCell>
							</TableRow>
						) : (
							rows.map((row, rowIndex) => {
								const rowKey = `${row.TensileRequirementsByGradeId ?? `new_${rowIndex}`}`;

								return (
									<TableRow
										key={rowKey}
										sx={{
											borderBottom: "1px solid rgba(224, 224, 224, 1)",
											"&:hover": {
												backgroundColor: "rgba(0, 0, 0, 0.04)",
											},
										}}
									>
										{columns.map((col, colIndex) => {
											const isSelected =
												selectedCell?.rowIndex === rowIndex &&
												selectedCell?.colIndex === colIndex;

											const needsWarning =
												col.field === "Elongation" &&
												row.ElongationLength != null &&
												row.ElongationLength !== "" &&
												!/\d/.test(row.ElongationLength);

											return (
												<TableCell
													key={col.field}
													data-cell-id={`${rowIndex}-${colIndex}`}
													align={col.align || "center"}
													onClick={(e) => {
														e.stopPropagation();
														handleCellClick(row, col, rowIndex, colIndex);
													}}
													onDoubleClick={(e) => {
														e.stopPropagation();
														handleCellDoubleClick(row, col, rowIndex, colIndex);
													}}
													sx={{
														fontSize: 12,
														lineHeight: 1.2,
														py: 0.25,
														px: 0.5,
														boxSizing: "border-box",
														whiteSpace: "nowrap",
														cursor: col.editable ? "cell" : "default",
														...(needsWarning && {
															backgroundColor: "#fff3cd",
														}),
														...(isSelected && {
															outline: `2px solid ${theme.palette.primary.main}`,
															outlineOffset: -2,
														}),
													}}
												>
													{renderCellContent(row, col, rowIndex)}
												</TableCell>
											);
										})}
									</TableRow>
								);
							})
						)}
					</TableBody>
				</Table>
			</TableContainer>
		</Paper>
	);
};
